import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  IconButton
} from '@mui/material';
import { 
  People as PeopleIcon,
  Email,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usersService, authService, assistantService } from '../utils/api';
import DashboardLayout from './DashboardLayout';

function Users() {
  const navigate = useNavigate();
  const { assistantId } = useParams();
  
  // Get user role for access control
  const userRole = authService.getUserRole();
  
  // Form state
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Assistant state
  const [currentAssistant, setCurrentAssistant] = useState(null);

  // Users table state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Load current assistant
  useEffect(() => {
    const assistant = assistantService.getSelectedAssistant();
    setCurrentAssistant(assistant);
    if (assistant) {
      fetchUsers();
    }
  }, []);

  // Fetch users
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await usersService.getAllUsers();
      setUsers(response.users || []);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    if (!role) newErrors.role = 'Role is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      await usersService.inviteUser(email, role, currentAssistant.id);
      toast.success(`Invitation sent to ${email}`);
      setEmail('');
      setRole('');
      setErrors({});
      fetchUsers(); // Refresh users list
    } catch (error) {
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'error';
      case 'admin':
        return 'primary';
      case 'moderator':
        return 'warning';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    return role.replace('_', ' ').toUpperCase();
  };

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
            Team Members
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
            Invite and manage team members
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {/* Invite User Card */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <PeopleIcon sx={{ mr: 1, color: '#3b82f6', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>
                    Invite User
                  </Typography>
                </Box>
                
                <form onSubmit={handleInvite}>
                  <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: '#94a3b8', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        borderRadius: '8px'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                  
                  <FormControl 
                    fullWidth 
                    sx={{ 
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        borderRadius: '8px'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem'
                      }
                    }} 
                    error={!!errors.role}
                  >
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={role}
                      label="Role"
                      onChange={(e) => setRole(e.target.value)}
                      disabled={loading}
                    >
                      <MenuItem value="admin" sx={{ fontSize: '0.875rem' }}>Admin</MenuItem>
                      <MenuItem value="moderator" sx={{ fontSize: '0.875rem' }}>Moderator</MenuItem>
                      <MenuItem value="viewer" sx={{ fontSize: '0.875rem' }}>Viewer</MenuItem>
                    </Select>
                    {errors.role && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {errors.role}
                      </Typography>
                    )}
                  </FormControl>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      bgcolor: '#334155',
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.25,
                      fontSize: '0.875rem',
                      borderRadius: '8px',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#1e293b',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send Invitation'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Grid>

          {/* Users List */}
          <Grid item xs={12} md={8}>
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>
                    Users & Invitations
                  </Typography>
                  <IconButton 
                    onClick={fetchUsers} 
                    disabled={usersLoading}
                    size="small"
                    sx={{ 
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      '&:hover': { bgcolor: '#f9fafb' }
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>

                {usersLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    <Table>
                      <TableHead sx={{ bgcolor: '#f9fafb' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', py: 1.5 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', py: 1.5 }}>Role</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', py: 1.5 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', py: 1.5 }}>Assistants</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                No users found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          users.map((user, index) => (
                            <TableRow key={index} sx={{ '&:last-child td': { border: 0 } }}>
                              <TableCell sx={{ py: 2, fontSize: '0.875rem', color: '#0f172a' }}>{user.email}</TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Chip 
                                  label={getRoleLabel(user.role)} 
                                  color={getRoleColor(user.role)}
                                  size="small"
                                  sx={{ 
                                    fontWeight: 600, 
                                    fontSize: '0.7rem',
                                    height: 22,
                                    borderRadius: '4px'
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Chip 
                                  label={user.invitation_status || 'Active'} 
                                  color={user.invitation_status === 'pending' ? 'warning' : 'success'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ 
                                    fontWeight: 600, 
                                    fontSize: '0.7rem',
                                    height: 22,
                                    borderRadius: '4px'
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                {user.assistants && user.assistants.length > 0 ? (
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {user.assistants.map((asst, idx) => (
                                      <Chip 
                                        key={idx}
                                        label={asst.assistant_name || asst.assistant_id}
                                        size="small"
                                        variant="outlined"
                                        sx={{ 
                                          fontSize: '0.7rem',
                                          height: 22,
                                          borderRadius: '4px'
                                        }}
                                      />
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                                    None
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </DashboardLayout>
  );
}

export default Users;
